// useTimeWatcher.js
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';

import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

/**
 * React hook that evaluates a time-based condition at a fixed interval.
 *
 * Useful for things like checking if a timestamp has expired,
 * counting down remaining time, or any logic that needs to be recalculated
 * regularly based on the current time.
 *
 * @param {function({ nowUnixTimestamp: number }): any} checker - A callback function that receives the current (possibly offset) UNIX timestamp (in seconds) and returns a computed value.
 * @param {number} [interval=1000] - Interval in milliseconds for how often to re-evaluate the callback.
 * @returns {*} The latest value returned by the callback.
 *
 * @example
 * // Check if a token is expired
 * const isExpired = useTimeWatcher(({ nowUnixTimestamp }) => nowUnixTimestamp >= tokenExpiryTime);
 */
const useTimeWatcher = (checker = NULL_FUNCTION, interval = 1000) => {
  const timestampOffsetSeconds =
    useSelector(state => getTimestampOffset(state, Accuracy.SECOND)) || 0;
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick(tick => tick + 1), interval);
    return () => clearInterval(timer);
  }, [interval]);

  return checker({
    nowUnixTimestamp: getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    }),
  });
};

export default useTimeWatcher;
